/**
 * zh-CN catalogue `templates` (W3-09; English: src/core/tools/templates.messages.ts and
 * src/features/easing/templates.messages.ts; terms: docs/i18n/glossary.md).
 */
import type { MessageCatalog } from '../../core/util/types';

const catalog: MessageCatalog = {
  ns: 'templates',
  locale: 'zh-CN',
  messages: {
    'reason.lineMissing': '该判定线已不存在。',
    'reason.durationInvalid': '模板需要大于零的时长，并且从第 0 拍或之后开始。',
    'reason.notLayered': '此模板会改变尺寸或颜色，而它们没有层级：不能放在新层级上。',
    'reason.paramInvalid': '“{name}”的值在此处不可用。',
    'reason.colorInvalid': '请选择一种颜色，例如 #ff8000。',
    'reason.pointMissing': '请选择要移动到的点。',
    'note.thicknessSkipped': '普通判定线的粗细没有一个看起来等同于“无关键帧”的值，因此保持不变：只有长度会脉动。',
    'note.colorFixed':
      '该判定线现在有颜色关键帧：Phira 不再在其上显示 AP/FC 指示颜色，闪烁前后会显示默认的判定线颜色。',
    'note.noParent': '该判定线没有父线：它将绕屏幕中心旋转。',
    'note.xyLinked': '该判定线的 X 和 Y 位置已关联：另一个轴在相同拍数处得到了关键帧，并保持其运动。',
    'note.otherLayers': '此属性的其他层级仍会叠加到结果上。',
    'note.baseChanges': '新层级下方的关键帧从第 {beat} 拍起再次变化：偏移量保持不变，因此结果从那里起随之移动。',
    'note.alphaNegative':
      '新层级下方的不透明度从第 {beat} 拍起再次变化，相加后小于 0：Phira 会在那里隐藏判定线及其音符。请不使用新层级来应用，或在该拍为新层级添加关键帧。',
    title: '运动模板',
    target: '判定线 {line}，从 {beat} 开始',
    chooserLabel: '模板',
    'name.fadeIn': '淡入',
    'name.fadeOut': '淡出',
    'name.slideIn': '滑入',
    'name.slideOut': '滑出',
    'name.moveTo': '移动到此处',
    'name.spin': '旋转',
    'name.swing': '摇摆',
    'name.pulse': '脉动',
    'name.shake': '抖动',
    'name.flash': '闪烁颜色',
    'name.orbitParent': '环绕',
    'about.fadeIn': '不透明度从起始值升至目标值，然后保持。',
    'about.fadeOut': '不透明度降至目标值，然后保持。',
    'about.slideIn': '判定线从一侧（或一个点）进入，停在它原本的位置。',
    'about.slideOut': '判定线向一侧（或一个点）离开，并停在那里。',
    'about.moveTo': '判定线移动到一个点并停在那里。',
    'about.spin': '判定线旋转整圈或部分圈数，并保持新的角度。',
    'about.swing': '判定线以精确的正弦波围绕其角度来回摆动，然后回到原位。',
    'about.pulse': '判定线在每次脉动时变大，再缓动回原来的尺寸。',
    'about.shake': '判定线围绕其位置抖动（相同种子产生相同图案），然后回到原位。',
    'about.flash': '颜色跳变为闪烁颜色，再缓动回原色。',
    'about.orbitParent': '判定线以当前距离环绕其父线（或屏幕中心）。',
    start: '开始',
    duration: '时长',
    curve: '缓动',
    curveChange: '更改缓动',
    curveFixed: '此模板自行决定运动的形状。',
    from: '从',
    to: '到',
    opacityFrom: '起始不透明度',
    opacityTo: '目标不透明度',
    side: '方向',
    sideIn: '进入方向',
    sideOut: '离开方向',
    'side.left': '左',
    'side.right': '右',
    'side.top': '上',
    'side.bottom': '下',
    'side.point': '一个点',
    distance: '距离',
    pointX: '点 X',
    pointY: '点 Y',
    usePosition: '使用判定线的位置',
    turns: '圈数',
    direction: '方向',
    clockwise: '顺时针',
    counterClockwise: '逆时针',
    amplitude: '幅度',
    swings: '摆动次数',
    settle: '接近结尾时逐渐平息',
    amount: '峰值尺寸',
    pulses: '脉动次数',
    axes: '轴',
    'axes.both': '两者',
    'axes.x': '仅 X',
    'axes.y': '仅 Y',
    shakes: '抖动次数',
    seed: '图案',
    newSeed: '新图案',
    color: '闪烁颜色',
    radius: '半径',
    radiusHelp: '0 表示保持判定线到其父线的当前距离。',
    asNewLayer: '作为新层级应用',
    asNewLayerHelp: '运动会叠加在现有关键帧之上，现有关键帧保持不变。',
    asNewLayerNo: '尺寸和颜色没有层级。',
    previewNote: '预览会循环播放这段范围。应用之前不会做任何更改。',
    previewOff: '在预览中循环播放这段范围',
    apply: '应用',
    cancel: '取消',
    applied: '已添加“{name}”：{count, plural, other {# 个关键帧}}。',
    failed: '无法应用此模板：{reason}',
    noLine: '请先选择一条判定线。',
    txApply: '模板：{name}',
    cmdOpen: '运动模板…',
    cmdTemplate: '模板：{name}…',
    'cmd.fadeIn': '模板：淡入…',
    'cmd.fadeOut': '模板：淡出…',
    'cmd.slideIn': '模板：滑入…',
    'cmd.slideOut': '模板：滑出…',
    'cmd.moveTo': '模板：移动到此处…',
    'cmd.spin': '模板：旋转…',
    'cmd.swing': '模板：摇摆…',
    'cmd.pulse': '模板：脉动…',
    'cmd.shake': '模板：抖动…',
    'cmd.flash': '模板：闪烁颜色…',
    'cmd.orbitParent': '模板：环绕…',
  },
};

export default catalog;
